import { API_VERSION, GAME_ID, GAME_VERSION, LEGACY_VERSION } from "./manifest.generated.js";
/** Public, JSON-safe API v1 contract. Money is serialized as a decimal string. */
export const GAME_MANIFEST = Object.freeze({ gameId: GAME_ID, gameVersion: GAME_VERSION, apiVersion: API_VERSION, legacyVersion: LEGACY_VERSION });
export class ContractValidationError extends Error {
    constructor(message) { super(message); this.name = "ContractValidationError"; }
}
const isRecord = (value) => !!value && typeof value === "object" && !Array.isArray(value);
const isString = (value) => typeof value === "string";
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const DECIMAL_RE = /^(0|[1-9][0-9]*)$/;
export function assertCreditAmount(value, field = "amount") {
    if (!isString(value) || !DECIMAL_RE.test(value))
        throw new ContractValidationError(`${field} must be a non-negative decimal string`);
}
export function assertSignedCreditAmount(value, field = "amount") {
    if (!isString(value) || !/^-?(0|[1-9][0-9]*)$/.test(value))
        throw new ContractValidationError(`${field} must be a signed decimal string`);
}
export function assertPrizeRedemptionRequest(value) {
    if (!isRecord(value) || !isString(value.prizeId) || !UUID_RE.test(value.prizeId) || !isString(value.idempotencyKey) || !UUID_RE.test(value.idempotencyKey))
        throw new ContractValidationError("invalid prize redemption request");
    if (Object.keys(value).length !== 2)
        throw new ContractValidationError("invalid prize redemption request");
}
export function parseCreditAmount(value) { assertCreditAmount(value); return BigInt(value); }
export function formatCreditAmount(value) { if (value < 0n)
    throw new ContractValidationError("credit cannot be negative"); return value.toString(); }
export function assertSpinRequest(value) {
    if (!isRecord(value))
        throw new ContractValidationError("invalid spin request envelope");
    if (!isString(value.sessionId) || !isString(value.idempotencyKey) || !UUID_RE.test(value.idempotencyKey) || typeof value.expectedRevision !== "number" || !Number.isSafeInteger(value.expectedRevision) || value.expectedRevision < 0 || !isRecord(value.bet))
        throw new ContractValidationError("invalid spin request envelope");
    if (![1, 2, 5, 10].includes(value.bet.coin) || ![1, 2, 3, 4].includes(value.bet.garden))
        throw new ContractValidationError("coin/garden is not an allowed wager");
    for (const prohibited of ["win", "payout", "balance", "grid", "userId", "user_id", "result"])
        if (prohibited in value)
            throw new ContractValidationError(`client assertion '${prohibited}' is forbidden`);
}
export function assertDoubleStartRequest(value) {
    if (!isRecord(value))
        throw new ContractValidationError("invalid double start request");
    if (!isString(value.sessionId) || !isString(value.idempotencyKey) || !UUID_RE.test(value.idempotencyKey) || typeof value.expectedRevision !== "number" || !Number.isSafeInteger(value.expectedRevision) || value.expectedRevision < 0)
        throw new ContractValidationError("invalid double start request");
    if (value.mode !== "full" && value.mode !== "half")
        throw new ContractValidationError("double mode must be full or half");
    for (const prohibited of ["amount", "win", "payout", "balance", "userId", "user_id", "result"])
        if (prohibited in value)
            throw new ContractValidationError(`client assertion '${prohibited}' is forbidden`);
}
export function assertDoubleActionRequest(value) {
    if (!isRecord(value))
        throw new ContractValidationError("invalid double action request");
    if (!isString(value.sessionId) || !isString(value.idempotencyKey) || !UUID_RE.test(value.idempotencyKey) || typeof value.expectedRevision !== "number" || !Number.isSafeInteger(value.expectedRevision) || value.expectedRevision < 0 || ![0, 1, 2].includes(value.selectedReel))
        throw new ContractValidationError("invalid double action request");
    for (const prohibited of ["win", "payout", "balance", "userId", "user_id", "result"])
        if (prohibited in value)
            throw new ContractValidationError(`client assertion '${prohibited}' is forbidden`);
}
export function assertDoubleCollectRequest(value) {
    if (!isRecord(value) || !isString(value.sessionId) || !isString(value.idempotencyKey) || !UUID_RE.test(value.idempotencyKey) || typeof value.expectedRevision !== "number" || !Number.isSafeInteger(value.expectedRevision) || value.expectedRevision < 0)
        throw new ContractValidationError("invalid double collect request");
    for (const prohibited of ["amount", "win", "payout", "balance", "pot", "userId", "user_id", "result"])
        if (prohibited in value)
            throw new ContractValidationError(`client assertion '${prohibited}' is forbidden`);
}
/** A sandboxed frame may act only on the session explicitly bootstrapped by its parent. */
export function assertActiveSessionRequest(request, activeSessionId) {
    if (!activeSessionId || request.sessionId !== activeSessionId)
        throw new ContractValidationError("request session does not match the active iframe session");
}
export function assertIframeMessage(value) {
    if (!isRecord(value) || !isString(value.type) || !isString(value.nonce) || value.nonce.length < 24 || value.nonce.length > 256)
        throw new ContractValidationError("invalid iframe message");
    if (value.type === "wb:ready" && isString(value.sessionId))
        return;
    if (value.type === "wb:request-spin")
        return assertSpinRequest(value.request);
    if (value.type === "wb:request-double-start")
        return assertDoubleStartRequest(value.request);
    if (value.type === "wb:request-double-action")
        return assertDoubleActionRequest(value.request);
    if (value.type === "wb:request-double-collect")
        return assertDoubleCollectRequest(value.request);
    if (value.type === "wb:response" && isString(value.requestId) && typeof value.ok === "boolean" && (value.error === undefined || isString(value.error)))
        return;
    throw new ContractValidationError("unsupported iframe message type");
}
/** Stable serialization makes idempotency/request and outcome hashes portable across Deno and browsers. */
export function canonicalJson(value) {
    if (value === null || typeof value === "boolean" || typeof value === "number" || typeof value === "string")
        return JSON.stringify(value);
    if (typeof value === "bigint")
        return JSON.stringify(value.toString());
    if (Array.isArray(value))
        return `[${value.map(canonicalJson).join(",")}]`;
    if (isRecord(value))
        return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${canonicalJson(value[key])}`).join(",")}}`;
    throw new ContractValidationError("hash input must be JSON data");
}
export async function sha256Hex(value) {
    const bytes = new TextEncoder().encode(canonicalJson(value));
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    return [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, "0")).join("");
}
export const hashSpinRequest = (request) => sha256Hex({ sessionId: request.sessionId, expectedRevision: request.expectedRevision, bet: request.bet });
//# sourceMappingURL=index.js.map